import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Grid,
  Divider,
  Button,
  Dialog,
  Typography,
  Box,
} from '@mui/material';

import TextField from '../text-field/TextField';
import Select from '../select/Select';
import ArrowButton from '../UI/go-back/GoBack';
import CollectionsConfigurate from './CollectionsConfigurate';

import success from '../../icons/success.svg';

import './Collections.scss';
import appStyles from '../app/App.module.scss';

import {
  useGetAuthenticationQuery,
  useGetCollectionsQuery,
  useUpdateCollectionMutation,
  useGetProfilesQuery,
} from '../../services/ADapi';

const CollectionsEditForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    data: { user_id },
  } = useGetAuthenticationQuery();
  const selectedUserIds = useSelector(
    (state) => state.tree.selectedUsersIdsForBackend
  );

  const { data: profiles = [] } = useGetProfilesQuery(user_id);

  const { data: collectionById = {} } = useGetCollectionsQuery(user_id, {
    selectFromResult: ({ data, isLoading }) => {
      const collectionId = +id;

      return {
        isLoading,
        data: data?.find((collection) => collection.id === collectionId),
      };
    },
  });

  const [updateCollection] = useUpdateCollectionMutation();

  const [name, setName] = useState(collectionById.name);
  const [nameIsBlur, setNameIsBlur] = useState(false);
  const [nameError, setNameError] = useState(
    'Collection name field is required'
  );
  const [policy, setPolicy] = useState(collectionById.profile);
  const [checkedUsers, setCheckedUsers] = useState([]);
  const [formIsValid, setFormIsValid] = useState(false);
  const [formIsSuccess, setFormIsSuccess] = useState(false);
  const [updateCollectionData, setCreateCollectionData] = useState({
    plan_code: 'RESELLER',
    id: +id,
    priority: collectionById.priority,
    profile: collectionById.profile,
    domains: [],
    groups: [],
    ad_users: collectionById.ad_users,
  });
  const [isDialogShow, setDialogState] = useState(false);

  useEffect(() => {
    setCheckedUsers(collectionById.ad_users);
  }, [collectionById]);

  useEffect(() => {
    nameError ? setFormIsValid(false) : setFormIsValid(true);
  }, [nameError]);

  useEffect(() => {
    setCreateCollectionData({
      ...updateCollectionData,
      ad_users: selectedUserIds,
    });
    setFormIsValid(true);

    // eslint-disable-next-line
  }, [selectedUserIds]);

  const nameHandler = (e) => {
    setName(e.target.value);

    if (!e.target.value) {
      setNameError('Name field is required');
    } else {
      setNameError('');
      setCreateCollectionData({
        ...updateCollectionData,
        name: e.target.value,
      });
    }
  };

  const policyHandler = (e) => {
    setPolicy(e.target.value);

    setCreateCollectionData({
      ...updateCollectionData,
      profile: e.target.value,
    });
  };

  const blurHandler = (e) => {
    switch (e.target.name) {
      case 'name':
        setNameIsBlur(true);
        break;
      default:
        break;
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();

    try {
      await updateCollection({
        user_id,
        payload: updateCollectionData,
      }).unwrap();
    } catch (error) {
      console.error(error);
    }

    setFormIsSuccess(!formIsSuccess);
    setFormIsValid(false);
    setNameError(' ');
    dialogStateHandler();
  };

  const dialogStateHandler = () => {
    if (isDialogShow) {
      navigate('/collections');
    }

    setDialogState((prevState) => !prevState);
  };

  const successCircleCls = appStyles['circle'] + ' ' + appStyles['success'];

  return (
    <div className="collections-form-wrapper">
      <ArrowButton
        url="/collections"
        direction="left"
        title="Edit collection"
      />
      <Paper sx={{ position: 'relative' }}>
        <Grid
          container
          spacing={1}
          pb={3}
          component="form"
          alignItems="flex-end"
        >
          <Grid item xs={12} md={3}>
            <TextField
              label="Name"
              value={name}
              size="small"
              placeholder="Collection name"
              onChange={nameHandler}
              onBlur={blurHandler}
            />
            {nameIsBlur && nameError && (
              <span className="validate-error">{nameError}</span>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <Select
              value={policy}
              label="Policy"
              size="small"
              onChange={policyHandler}
            >
              {profiles.map((option) => (
                <option key={Math.random()} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Divider />
        <CollectionsConfigurate
          collectionName={name}
          checkedUsers={checkedUsers}
        />
        <div className="collections-form-create">
          <span className="collections-form-create-counter">
            Selected: {selectedUserIds.length} Users
          </span>
          <Button
            variant="contained"
            disabled={!formIsValid}
            size="small"
            onClick={submitForm}
          >
            Save collection
          </Button>
        </div>
      </Paper>

      {isDialogShow ? (
        <Dialog open={true}>
          <Box className={successCircleCls} sx={{ alignSelf: 'center' }}>
            <img
              src={success}
              width={28}
              height={28}
              alt="Collection Success"
            />
          </Box>
          <Typography variant="h6" sx={{ pt: '12px', pb: 3 }}>
            Collection updated
          </Typography>
          <ul className="collections-create-success-list">
            <li>{name}</li>
            <li>{policy}</li>
            <li>{selectedUserIds.length} Users</li>
          </ul>
          <Button variant="contained" size="small" onClick={dialogStateHandler}>
            Got it
          </Button>
        </Dialog>
      ) : null}
    </div>
  );
};

export default CollectionsEditForm;
